import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CustomVerticalIcon } from "@/components/crayons-code-logo/CustomIcon.vertical";
import { applyServerErrors } from "@/lib/apply-server-errors";
import {
  verifyOtpSchema,
  type VerifyOtpFormValues,
  VERIFY_OTP_FIELDS,
} from "@/schemas/auth";

export const Route = createFileRoute("/(auth)/verify-otp")({
  component: VerifyOTP,
});

function VerifyOTP() {
  const [isVerifying, setIsVerifying] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<VerifyOtpFormValues>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { otp: "" },
  });
  const otp = watch("otp");

  const onValid = async (data: VerifyOtpFormValues) => {
    setIsVerifying(true);
    try {
      // No backend endpoint exists for OTP verification yet — this stays a
      // stub, but is wired for applyServerErrors so it works once the API lands.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success(`OTP ${data.otp} verified successfully!`);
    } catch (err) {
      const { handled, rest } = applyServerErrors(err, setError, VERIFY_OTP_FIELDS);
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = () => {
    toast.info("A new OTP has been sent to your email");
  };

  return (
    <div className="grid min-h-svh bg-muted">
      {/* Left side - Form */}
      <div className="flex flex-col gap-4 p-6 md:p-10 h-screen">
        {/* Form Container */}
        <div className="flex flex-1 flex-col gap-6 items-center justify-center">
          <h1>
            <div className="flex items-center justify-center gap-2 font-medium text-xl pb-2">
              {/* <CustomIcon size={250} /> */}
              <CustomVerticalIcon size={180} />
            </div>
          </h1>
          <Card className="w-full max-w-sm text-center shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">
                Verify Your Account
              </CardTitle>
              <CardDescription className="pt-2">
                Enter the 6-digit verification code sent to your email
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onValid)} className="space-y-6">
                <div className="flex justify-center">
                  <Controller
                    control={control}
                    name="otp"
                    render={({ field }) => (
                      <InputOTP
                        maxLength={6}
                        value={field.value}
                        onChange={field.onChange}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    )}
                  />
                </div>
                {errors.otp && (
                  <p className="text-xs text-red-500 text-center">{errors.otp.message}</p>
                )}

                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isVerifying || otp.length !== 6}
                  >
                    {isVerifying ? "Verifying..." : "Verify OTP"}
                  </Button>

                  <p className="text-sm text-muted-foreground">
                    Didn't receive the code?{" "}
                    <button
                      type="button"
                      onClick={handleResend}
                      className="text-primary hover:underline font-medium"
                    >
                      Resend OTP
                    </button>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
          <div className="text-muted-foreground text-center text-xs text-balance">
            By clicking continue, you agree to our{" "}
            <Link
              to="/terms-of-service"
              className="underline underline-offset-4 hover:text-primary"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy-policy"
              className="underline underline-offset-4 hover:text-2rimary"
            >
              Privacy Policy
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
